import type { QueryOptions } from '@tanstack/react-query';

import { http } from '@/api/instance';
import { getQueryParameters } from '@/api/query';

export const masterDataAPI = {
  activity_result: {
    list: ({ ...params }: Record<string, string | number>) =>
      http.get<ListResultsPageSize<MasterDataType>>('/api/info/activity_result', { params }).then(({ data }) => data),
    create: (data: MasterDataTypeForm) => http.post<unknown>(`/api/add/activity_result`, { data }),
    update: (id: number | string, data: MasterDataTypeForm) =>
      http.put<unknown>(`/api/edit/activity_result/${id}`, { data }),
    delete: (id: number | string) => http.delete<unknown>(`/api/delete/activity_result/${id}`),
  },

  activity_type: {
    list: ({ ...params }: Record<string, string | number>) =>
      http.get<ListResultsPageSize<MasterDataType>>('/api/info/activity_type', { params }).then(({ data }) => data),
    create: (data: MasterDataTypeForm) => http.post<unknown>(`/api/add/activity_type`, { data }),
    update: (id: number | string, data: MasterDataTypeForm) =>
      http.put<unknown>(`/api/edit/activity_type/${id}`, { data }),
    delete: (id: number | string) => http.delete<unknown>(`/api/delete/activity_type/${id}`),
  },

  product: {
    list: ({ ...params }: Record<string, string | number>) =>
      http.get<ListResultsPageSize<Product>>('/api/info/products', { params }).then(({ data }) => data),
    create: (data: MasterDataTypeForm) => http.post<unknown>(`/api/add/products`, { data }),
    update: (id: number | string, data: MasterDataTypeForm) => http.put<unknown>(`/api/edit/products/${id}`, { data }),
    delete: (id: number | string) => http.delete<unknown>(`/api/delete/products/${id}`),
  },

  product_type: {
    list: ({ ...params }: Record<string, string | number>) =>
      http.get<ListResultsPageSize<MasterDataType>>('/api/info/product_type', { params }).then(({ data }) => data),
    create: (data: MasterDataTypeForm) => http.post<unknown>(`/api/add/product_type`, { data }),
    update: (id: number | string, data: MasterDataTypeForm) =>
      http.put<unknown>(`/api/edit/product_type/${id}`, { data }),
    delete: (id: number | string) => http.delete<unknown>(`/api/delete/product_type/${id}`),
  },

  product_note: {
    list: ({ ...params }: Record<string, string | number>) =>
      http.get<ListResultsPageSize<MasterDataType>>('/api/info/product_note', { params }).then(({ data }) => data),
    create: (data: MasterDataTypeForm) => http.post<unknown>(`/api/add/product_note`, { data }),
    update: (id: number | string, data: MasterDataTypeForm) =>
      http.put<unknown>(`/api/edit/product_note/${id}`, { data }),
    delete: (id: number | string) => http.delete<unknown>(`/api/delete/product_note/${id}`),
  },

  lead_source: {
    list: ({ ...params }: Record<string, string | number>) =>
      http.get<ListResultsPageSize<MasterDataType>>('/api/info/lead_source', { params }).then(({ data }) => data),
    create: (data: MasterDataTypeForm) => http.post<unknown>(`/api/add/lead_source`, { data }),
    update: (id: number | string, data: MasterDataTypeForm) =>
      http.put<unknown>(`/api/edit/lead_source/${id}`, { data }),
    delete: (id: number | string) => http.delete<unknown>(`/api/delete/lead_source/${id}`),
  },

  contact_source: {
    list: ({ ...params }: Record<string, string | number>) =>
      http.get<ListResultsPageSize<MasterDataType>>('/api/info/contact_source', { params }).then(({ data }) => data),
    create: (data: MasterDataTypeForm) => http.post<unknown>(`/api/add/contact_source`, { data }),
    update: (id: number | string, data: MasterDataTypeForm) =>
      http.put<unknown>(`/api/edit/contact_source/${id}`, { data }),
    delete: (id: number | string) => http.delete<unknown>(`/api/delete/contact_source/${id}`),
  },

  work_office: {
    list: ({ ...params }: Record<string, string | number>) =>
      http.get<ListResultsPageSize<MasterDataType>>('/api/info/work_office', { params }).then(({ data }) => data),
    create: (data: MasterDataTypeForm) => http.post<unknown>(`/api/add/work_office`, { data }),
    update: (id: number | string, data: MasterDataTypeForm) =>
      http.put<unknown>(`/api/edit/work_office/${id}`, { data }),
    delete: (id: number | string) => http.delete<unknown>(`/api/delete/work_office/${id}`),
  },

  discount_reason: {
    list: ({ ...params }: Record<string, string | number>) =>
      http.get<ListResultsPageSize<MasterDataType>>('/api/info/discount_reason', { params }).then(({ data }) => data),
    create: (data: MasterDataTypeForm) => http.post<unknown>(`/api/add/discount_reason`, { data }),
    update: (id: number | string, data: MasterDataTypeForm) =>
      http.put<unknown>(`/api/edit/discount_reason/${id}`, { data }),
    delete: (id: number | string) => http.delete<unknown>(`/api/delete/discount_reason/${id}`),
  },

  shifts: {
    list: ({ ...params }: Record<string, string | number>) =>
      http.get<ListResultsPageSize<MasterDataType>>('/api/info/shifts', { params }).then(({ data }) => data),
    create: (data: MasterDataTypeForm) => http.post<unknown>(`/api/add/shifts`, { data }),
    update: (id: number | string, data: MasterDataTypeForm) => http.put<unknown>(`/api/edit/shifts/${id}`, { data }),
    delete: (id: number | string) => http.delete<unknown>(`/api/delete/shifts/${id}`),
  },

  classrooms: {
    list: ({ ...params }: Record<string, string | number>) =>
      http.get<ListResultsPageSize<Classroom>>('/api/info/classrooms', { params }).then(({ data }) => data),
    create: (data: MasterDataTypeForm) => http.post<unknown>(`/api/add/classrooms`, { data }),
    update: (id: number | string, data: MasterDataTypeForm) =>
      http.put<unknown>(`/api/edit/classrooms/${id}`, { data }),
    delete: (id: number | string) => http.delete<unknown>(`/api/delete/classrooms/${id}`),
    list_available: (params: AvailableClassroomParams) =>
      http
        .get<ListResultsPageSize<AvailableClassroom>>('/api/info/classrooms/available', { params })
        .then(({ data }) => data),
  },

  department: {
    list: ({ ...params }: Record<string, string | number>) =>
      http.get<ListResultsPageSize<MasterDataType>>('/api/info/departments', { params }).then(({ data }) => data),
    create: (data: MasterDataTypeForm) => http.post<unknown>(`/api/add/departments`, { data }),
    update: (id: number | string, data: MasterDataTypeForm) =>
      http.put<unknown>(`/api/edit/departments/${id}`, { data }),
    delete: (id: number | string) => http.delete<unknown>(`/api/delete/departments/${id}`),
  },

  class_score: {
    list: ({ ...params }: Record<string, string | number>) =>
      http.get<ListResultsPageSize<MasterDataType>>('/api/info/class_score', { params }).then(({ data }) => data),
    create: (data: MasterDataTypeForm) => http.post<unknown>(`/api/add/class_score`, { data }),
    update: (id: number | string, data: MasterDataTypeForm) =>
      http.put<unknown>(`/api/edit/class_score/${id}`, { data }),
    delete: (id: number | string) => http.delete<unknown>(`/api/delete/class_score/${id}`),
  },

  supplies: {
    list: ({ ...params }: Record<string, string | number>) =>
      http.get<ListResultsPageSize<MasterDataType>>('/api/info/supplies', { params }).then(({ data }) => data),
    create: (data: MasterDataTypeForm) => http.post<unknown>(`/api/add/supplies`, { data }),
    update: (id: number | string, data: MasterDataTypeForm) => http.put<unknown>(`/api/edit/supplies/${id}`, { data }),
    delete: (id: number | string) => http.delete<unknown>(`/api/delete/supplies/${id}`),
  },

  sale_agency: {
    list: ({ ...params }: Record<string, string | number>) =>
      http.get<ListResultsPageSize<MasterDataType>>('/api/info/sale_agency', { params }).then(({ data }) => data),
    create: (data: MasterDataTypeForm) => http.post<unknown>(`/api/add/sale_agency`, { data }),
    update: (id: number | string, data: MasterDataTypeForm) =>
      http.put<unknown>(`/api/edit/sale_agency/${id}`, { data }),
    delete: (id: number | string) => http.delete<unknown>(`/api/delete/sale_agency/${id}`),
  },

  province: {
    list: ({ ...params }: Record<string, string | number>) =>
      http.get<ListResultsPageSize<MasterDataType>>('/api/info/province', { params }).then(({ data }) => data),
    create: (data: MasterDataTypeForm) => http.post<unknown>(`/api/add/province`, { data }),
    update: (id: number | string, data: MasterDataTypeForm) => http.put<unknown>(`/api/edit/province/${id}`, { data }),
    delete: (id: number | string) => http.delete<unknown>(`/api/delete/province/${id}`),
  },
};

export const masterDataQuery = {
  activity_result: {
    list: (params: QueryParams<Partial<SearchFormData>>) =>
      ({
        queryKey: ['GetActivityResults', params] as const,
        queryFn: () => masterDataAPI.activity_result.list(getQueryParameters(params)),
      }) as const satisfies QueryOptions,
  },
  activity_type: {
    list: (params: QueryParams<Partial<SearchFormData>>) =>
      ({
        queryKey: ['GetActivityTypes', params] as const,
        queryFn: () => masterDataAPI.activity_type.list(getQueryParameters(params)),
      }) as const satisfies QueryOptions,
  },
  product: {
    list: (params: QueryParams<Partial<SearchFormData>>) =>
      ({
        queryKey: ['GetProducts', params] as const,
        queryFn: () => masterDataAPI.product.list(getQueryParameters(params)),
      }) as const satisfies QueryOptions,
  },
  product_type: {
    list: (params: QueryParams<Partial<SearchFormData>>) =>
      ({
        queryKey: ['GetProductType', params] as const,
        queryFn: () => masterDataAPI.product_type.list(getQueryParameters(params)),
      }) as const satisfies QueryOptions,
  },
  product_note: {
    list: (params: QueryParams<Partial<SearchFormData>>) =>
      ({
        queryKey: ['GetProductNote', params] as const,
        queryFn: () => masterDataAPI.product_note.list(getQueryParameters(params)),
      }) as const satisfies QueryOptions,
  },
  lead_source: {
    list: (params: QueryParams<Partial<SearchFormData>>) =>
      ({
        queryKey: ['GetLeadSource', params] as const,
        queryFn: () => masterDataAPI.lead_source.list(getQueryParameters(params)),
      }) as const satisfies QueryOptions,
  },
  contact_source: {
    list: (params: QueryParams<Partial<SearchFormData>>) =>
      ({
        queryKey: ['GetContactSource', params] as const,
        queryFn: () => masterDataAPI.contact_source.list(getQueryParameters(params)),
      }) as const satisfies QueryOptions,
  },
  work_office: {
    list: (params: QueryParams<Partial<SearchFormData>>) =>
      ({
        queryKey: ['GetLearningLocation', params] as const,
        queryFn: () => masterDataAPI.work_office.list(getQueryParameters(params)),
      }) as const satisfies QueryOptions,
  },
  discount_reason: {
    list: (params: QueryParams<Partial<SearchFormData>>) =>
      ({
        queryKey: ['GetDiscountReason', params] as const,
        queryFn: () => masterDataAPI.discount_reason.list(getQueryParameters(params)),
      }) as const satisfies QueryOptions,
  },
  shifts: {
    list: (params: QueryParams<Partial<SearchFormData>>) =>
      ({
        queryKey: ['GetShifts', params] as const,
        queryFn: () => masterDataAPI.shifts.list(getQueryParameters(params)),
      }) as const satisfies QueryOptions,
  },
  classrooms: {
    list: (params: QueryParams<Partial<SearchFormData>>) =>
      ({
        queryKey: ['GetClassRooms', params] as const,
        queryFn: () => masterDataAPI.classrooms.list(getQueryParameters(params)),
      }) as const satisfies QueryOptions,
    list_available_classrooms: (params: AvailableClassroomParams) =>
      ({
        queryKey: ['GetAvailableClassRooms', params] as const,
        queryFn: () => masterDataAPI.classrooms.list_available(params),
      }) as const satisfies QueryOptions,
  },
  department: {
    list: (params: QueryParams<Partial<SearchFormData>>) =>
      ({
        queryKey: ['GetDepartment', params] as const,
        queryFn: () => masterDataAPI.department.list(getQueryParameters(params)),
      }) as const satisfies QueryOptions,
  },
  class_score: {
    list: (params: QueryParams<Partial<SearchFormData>>) =>
      ({
        queryKey: ['GetClassScore', params] as const,
        queryFn: () => masterDataAPI.class_score.list(getQueryParameters(params)),
      }) as const satisfies QueryOptions,
  },
  supplies: {
    list: (params: QueryParams<Partial<SearchFormData>>) =>
      ({
        queryKey: ['GetSupplies', params] as const,
        queryFn: () => masterDataAPI.supplies.list(getQueryParameters(params)),
      }) as const satisfies QueryOptions,
  },
  sale_agency: {
    list: (params: QueryParams<Partial<SearchFormData>>) =>
      ({
        queryKey: ['GetSaleAgency', params] as const,
        queryFn: () => masterDataAPI.sale_agency.list(getQueryParameters(params)),
      }) as const satisfies QueryOptions,
  },
  province: {
    list: (params: QueryParams<Partial<SearchFormData>>) =>
      ({
        queryKey: ['GetSaleAgency', params] as const,
        queryFn: () => masterDataAPI.province.list(getQueryParameters(params)),
      }) as const satisfies QueryOptions,
  },
};
